import { UTCDateMini } from '@date-fns/utc';
import {
  PatientSummaryDto,
  PatientSummaryDtoContactTracing,
  PatientSummaryDtoContactTracingStatus,
  TherapyDto,
} from '../../api/generated/model';
import { PatientTableFilter, PatientTableView } from '../../state/patientSearchAtoms.ts';
import { getLatestTherapyCondition } from '../condition.ts';
import { getCurrentDate, getDifferenceInDays } from '../date.ts';
import { hasDirectlyObservedTherapy } from '../therapy.ts';

export function getPatientTableViewName(tableFilter: PatientTableView) {
  switch (tableFilter) {
    case PatientTableView.FINDINGS:
      return 'Befund';
    case PatientTableView.THERAPY:
      return 'Therapie';
    case PatientTableView.CONTACT_TRACING:
      return 'Umgebungsuntersuchung';
    case PatientTableView.SCREENING:
      return 'Screening';
  }
}

export function filterPatientsForTable(
  patients: PatientSummaryDto[],
  selectedTableView: PatientTableView,
  selectedFilter: PatientTableFilter[],
) {
  switch (selectedTableView) {
    case PatientTableView.FINDINGS: {
      const patientsForFindingsTable = patients.filter((patient) => patient.caseNumber);
      return patientsForFindingsTable.filter((patient) => {
        return selectedFilter.every((filter) => {
          return findingsTableFilterCondition(filter, patient);
        });
      });
    }
    case PatientTableView.THERAPY: {
      const patientsForTherapyTable = patients.filter((patient) => patient.caseNumber);
      return patientsForTherapyTable.filter((patient) => {
        return selectedFilter.every((filter) => {
          return therapyTableFilterCondition(filter, patient);
        });
      });
    }
    case PatientTableView.CONTACT_TRACING: {
      const patientsForContactTracingTable = patients.filter((patient) => patient.caseNumber);
      return patientsForContactTracingTable.filter((patient) => {
        return selectedFilter.every((filter) => {
          return contactTracingTableFilterCondition(filter, patient);
        });
      });
    }
    case PatientTableView.SCREENING: {
      const patientsForScreeningTable = patients.filter((patient) => !patient.caseNumber);
      return patientsForScreeningTable.filter((patient) => {
        return selectedFilter.every((filter) => {
          return screeningTableFilterCondition(filter, patient);
        });
      });
    }
  }
}

function findingsTableFilterCondition(filter: PatientTableFilter, patient: PatientSummaryDto) {
  switch (filter) {
    case PatientTableFilter.FINDINGS_WITH_CLINICAL_REPORT:
      return !!patient.clinicalReport;
    case PatientTableFilter.FINDINGS_WITHOUT_CLINICAL_REPORT:
      return !patient.clinicalReport;
    case PatientTableFilter.FINDINGS_WITH_LAB_REPORT:
      return patient.countLabReports > 0;
    case PatientTableFilter.FINDINGS_WITHOUT_LAB_REPORT:
      return patient.countLabReports <= 0;
    default:
      return true;
  }
}

function therapyTableFilterCondition(filter: PatientTableFilter, patient: PatientSummaryDto) {
  switch (filter) {
    case PatientTableFilter.THERAPY_NO_THERAPY:
      return getCurrentTherapyStatus(patient.therapy) === PatientTableFilter.THERAPY_NO_THERAPY;
    case PatientTableFilter.THERAPY_PHASE1:
      return getCurrentTherapyStatus(patient.therapy) === PatientTableFilter.THERAPY_PHASE1;
    case PatientTableFilter.THERAPY_PHASE2:
      return getCurrentTherapyStatus(patient.therapy) === PatientTableFilter.THERAPY_PHASE2;
    case PatientTableFilter.THERAPY_ENDED_NOT_COMPLETED:
      return getCurrentTherapyStatus(patient.therapy) === PatientTableFilter.THERAPY_ENDED_NOT_COMPLETED;
    case PatientTableFilter.THERAPY_COMPLETED:
      return getCurrentTherapyStatus(patient.therapy) === PatientTableFilter.THERAPY_COMPLETED;
    case PatientTableFilter.THERAPY_DOT:
      return hasDirectlyObservedTherapy(patient.therapy?.directlyObservedTherapy);
    case PatientTableFilter.THERAPY_CONDITION_OVERDUE: {
      const latestConditionDate = getLatestTherapyCondition(patient.therapy?.therapyCondition)?.date;
      if (!latestConditionDate || !patient.therapy?.conditionIntervalDays) return false;

      return (
        getDifferenceInDays(latestConditionDate, getCurrentDate()) >= patient.therapy.conditionIntervalDays &&
        !patient.therapy.finished
      );
    }
    case PatientTableFilter.THERAPY_HOSPITALIZED:
      return patient.isHospitalized;
    case PatientTableFilter.THERAPY_ISOLATED:
      return patient.isIsolated;
    default:
      return true;
  }
}

function contactTracingTableFilterCondition(filter: PatientTableFilter, patient: PatientSummaryDto) {
  switch (filter) {
    case PatientTableFilter.CT_NOT_NEEDED:
      return patient.contactTracing === PatientSummaryDtoContactTracing.NO;
    case PatientTableFilter.CT_UNKNOWN:
      return patient.contactTracing === PatientSummaryDtoContactTracing.UNKNOWN;
    case PatientTableFilter.CT_NOT_STARTED:
      return (
        patient.contactTracing === PatientSummaryDtoContactTracing.YES &&
        patient.contactTracingStatus === PatientSummaryDtoContactTracingStatus.UNKNOWN
      );
    case PatientTableFilter.CT_COMPLETED:
      return (
        patient.contactTracing === PatientSummaryDtoContactTracing.YES &&
        patient.contactTracingStatus === PatientSummaryDtoContactTracingStatus.COMPLETED
      );
    case PatientTableFilter.CT_NOT_COMPLETED:
      return (
        patient.contactTracing === PatientSummaryDtoContactTracing.YES &&
        patient.contactTracingStatus === PatientSummaryDtoContactTracingStatus.NOT_COMPLETED
      );
    default:
      return true;
  }
}

function screeningTableFilterCondition(filter: PatientTableFilter, patient: PatientSummaryDto) {
  switch (filter) {
    case PatientTableFilter.SCREENING_WITH_CLINICAL_REPORT:
      return !!patient.clinicalReport;
    case PatientTableFilter.SCREENING_WITHOUT_CLINICAL_REPORT:
      return !patient.clinicalReport;
    default:
      return true;
  }
}

function getCurrentTherapyStatus(therapy: TherapyDto | null | undefined) {
  if (!therapy?.startPhase1 || !therapy.end) return PatientTableFilter.THERAPY_NO_THERAPY;
  if (therapy.finished) return PatientTableFilter.THERAPY_COMPLETED;

  const startPhase1Date = new UTCDateMini(therapy.startPhase1).getTime();
  const startPhase2Date = therapy.startPhase2 ? new UTCDateMini(therapy.startPhase2).getTime() : 0;
  const endDate = new UTCDateMini(therapy.end).getTime();
  const today = UTCDateMini.now();

  if (today > endDate && !therapy.finished) return PatientTableFilter.THERAPY_ENDED_NOT_COMPLETED;
  if (today < startPhase1Date) return PatientTableFilter.THERAPY_NO_THERAPY;
  if (today >= startPhase1Date && (today < startPhase2Date || startPhase2Date === 0))
    return PatientTableFilter.THERAPY_PHASE1;
  if (today >= startPhase2Date && startPhase2Date !== null) return PatientTableFilter.THERAPY_PHASE2;
}

export function getPatientTableFilterName(filter: PatientTableFilter) {
  switch (filter) {
    case PatientTableFilter.FINDINGS_WITH_CLINICAL_REPORT:
      return 'Mit MKB';
    case PatientTableFilter.FINDINGS_WITHOUT_CLINICAL_REPORT:
      return 'Ohne MKB';
    case PatientTableFilter.FINDINGS_WITH_LAB_REPORT:
      return 'Mit MLB';
    case PatientTableFilter.FINDINGS_WITHOUT_LAB_REPORT:
      return 'Ohne MLB';
    case PatientTableFilter.THERAPY_NO_THERAPY:
      return 'Ohne Therapie';
    case PatientTableFilter.THERAPY_PHASE1:
      return 'Phase 1';
    case PatientTableFilter.THERAPY_PHASE2:
      return 'Phase 2';
    case PatientTableFilter.THERAPY_ENDED_NOT_COMPLETED:
      return 'Enddatum überschritten';
    case PatientTableFilter.THERAPY_COMPLETED:
      return 'Abgeschlossen';
    case PatientTableFilter.THERAPY_DOT:
      return 'Mit DOT';
    case PatientTableFilter.THERAPY_CONDITION_OVERDUE:
      return 'Befinden überfällig';
    case PatientTableFilter.THERAPY_HOSPITALIZED:
      return 'Hospitalisiert';
    case PatientTableFilter.THERAPY_ISOLATED:
      return 'Isoliert';
    case PatientTableFilter.CT_NOT_NEEDED:
      return 'Nicht notwendig';
    case PatientTableFilter.CT_UNKNOWN:
      return 'Ohne Einschätzung';
    case PatientTableFilter.CT_NOT_STARTED:
      return 'Nicht gestartet';
    case PatientTableFilter.CT_COMPLETED:
      return 'Abgeschlossen';
    case PatientTableFilter.CT_NOT_COMPLETED:
      return 'In Arbeit';
    case PatientTableFilter.SCREENING_WITH_CLINICAL_REPORT:
      return 'Mit MKB';
    case PatientTableFilter.SCREENING_WITHOUT_CLINICAL_REPORT:
      return 'Ohne MKB';
  }
}

export const patientFindingsTableFilters = [
  PatientTableFilter.FINDINGS_WITH_CLINICAL_REPORT,
  PatientTableFilter.FINDINGS_WITHOUT_CLINICAL_REPORT,
  PatientTableFilter.FINDINGS_WITH_LAB_REPORT,
  PatientTableFilter.FINDINGS_WITHOUT_LAB_REPORT,
];

export const patientTherapyTableFilters = [
  PatientTableFilter.THERAPY_NO_THERAPY,
  PatientTableFilter.THERAPY_PHASE1,
  PatientTableFilter.THERAPY_PHASE2,
  PatientTableFilter.THERAPY_ENDED_NOT_COMPLETED,
  PatientTableFilter.THERAPY_COMPLETED,
  PatientTableFilter.THERAPY_DOT,
  PatientTableFilter.THERAPY_CONDITION_OVERDUE,
  PatientTableFilter.THERAPY_HOSPITALIZED,
  PatientTableFilter.THERAPY_ISOLATED,
];

export const patientContactTracingTableFilters = [
  PatientTableFilter.CT_NOT_NEEDED,
  PatientTableFilter.CT_UNKNOWN,
  PatientTableFilter.CT_NOT_STARTED,
  PatientTableFilter.CT_COMPLETED,
  PatientTableFilter.CT_NOT_COMPLETED,
];

export const patientScreeningTableFilters = [
  PatientTableFilter.SCREENING_WITH_CLINICAL_REPORT,
  PatientTableFilter.SCREENING_WITHOUT_CLINICAL_REPORT,
];

export function getFilterOptionsForPatientTableView(table: PatientTableView) {
  switch (table) {
    case PatientTableView.FINDINGS:
      return patientFindingsTableFilters;
    case PatientTableView.THERAPY:
      return patientTherapyTableFilters;
    case PatientTableView.CONTACT_TRACING:
      return patientContactTracingTableFilters;
    case PatientTableView.SCREENING:
      return patientScreeningTableFilters;
  }
}
